/**
 * Cliente HTTP del realm terminal (T018; implementation-notes 003 §4, contracts/terminal-api.md).
 *
 * Igual que el de Staff (`apps/staff/src/api/client.ts`) **sin** firma de dispositivo y **sin**
 * `credentials: 'include'` (research R-5: no hay cookies):
 * - `baseUrl = import.meta.env.VITE_API_ORIGIN ?? ''` (vacío en local: proxy de Vite `/api`).
 * - `Authorization: Bearer <token>` desde `credential/store.ts` cuando hay token; `Origin` lo pone
 *   el navegador. El token nunca va en la URL ni en logs.
 * - Mutaciones llevan `X-Requested-With: fermin-marker` (`TERMINAL_CSRF_HEADER_VALUE`).
 * - 2xx se valida con el esquema Zod de `@fermin/contracts`; `204`/cuerpo vacío → `undefined`.
 * - Errores → `ApiError` desde `ErrorEnvelopeSchema`. `401 UNAUTHENTICATED` y
 *   `401 TERMINAL_SESSION_REVOKED` **borran la credencial** y emiten el evento de `./events.ts`
 *   (`EVENT_BY_ERROR_CODE`); el root traduce el evento en navegación.
 * - `NetworkError` para fallos de red (sin servidor, DNS, CORS).
 *
 * Único módulo del marcador que llama a `fetch` (implementation-notes §4). El canal (WS/SSE) no
 * pasa por aquí: `channel/ws.ts` y `channel/sse.ts` abren sus propias conexiones.
 */
import {
  ErrorEnvelopeSchema,
  TERMINAL_CSRF_HEADER_VALUE,
  TerminalRevokeReasonSchema,
  type ErrorCode,
  type TerminalRevokeReason,
} from '@fermin/contracts';

import { clearCredential, getSessionToken } from '../credential/store';

import {
  emitFerminEvent,
  FERMIN_EVENTS,
  type FerminEventDetail,
  type FerminEventName,
} from './events';

import type { z } from 'zod';

/** Prefijo del realm terminal. Los métodos reciben rutas relativas a él: `api.get('/me', …)`. */
export const TERMINAL_API_PREFIX = '/api/v1/terminal';

/** Header anti-CSRF exigido por la API en toda mutación; valor `TERMINAL_CSRF_HEADER_VALUE`. */
export const CSRF_HEADER = 'X-Requested-With';
export const AUTHORIZATION_HEADER = 'Authorization';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

const MUTATING_METHODS: readonly HttpMethod[] = ['POST', 'PATCH', 'DELETE'];

/** Código de error → evento que el root traduce en navegación (`routes/__root.tsx`). */
export const EVENT_BY_ERROR_CODE: Readonly<Partial<Record<ErrorCode, FerminEventName>>> =
  Object.freeze({
    UNAUTHENTICATED: FERMIN_EVENTS.terminalUnauthenticated,
    TERMINAL_SESSION_REVOKED: FERMIN_EVENTS.terminalRevoked,
  });

/** Error normalizado del realm terminal: el sobre de `contracts/errors.md` como excepción tipada. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string;

  constructor(init: {
    code: ErrorCode;
    message: string;
    status: number;
    details?: Record<string, unknown> | undefined;
    requestId: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** `true` si la API respondió un código conocido del catálogo. */
  is(code: ErrorCode): boolean {
    return this.code === code;
  }

  /** `details.reason` de un `401 TERMINAL_SESSION_REVOKED`, o `null` si no aplica. */
  get revokeReason(): TerminalRevokeReason | null {
    return this.code === 'TERMINAL_SESSION_REVOKED' ? readRevokeReason(this.details) : null;
  }
}

/** `fetch` no llegó al servidor (sin red, DNS, CORS). La UI muestra `errors.NETWORK`. */
export class NetworkError extends Error {
  readonly status = 0;

  constructor(cause?: unknown) {
    super('No pudimos conectarnos. Revisa tu conexión a internet.', { cause });
    this.name = 'NetworkError';
  }
}

/** Lee `details.reason` con el contrato; cualquier otra cosa ⇒ `null`. */
export function readRevokeReason(
  details: Record<string, unknown> | undefined,
): TerminalRevokeReason | null {
  const parsed = TerminalRevokeReasonSchema.safeParse(details?.reason);
  return parsed.success ? parsed.data : null;
}

export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_ORIGIN ?? '';
}

/** Ruta absoluta sin origin: la que la API ve (`/api/v1/terminal/me`). */
export function resolvePath(path: string): string {
  return path.startsWith('/api/') ? path : `${TERMINAL_API_PREFIX}${path}`;
}

function buildUrl(resolvedPath: string): string {
  return `${getApiBaseUrl()}${resolvedPath}`;
}

/** Construye el `ApiError` a partir de la respuesta; tolera cuerpos que no son el sobre esperado. */
async function toApiError(response: Response): Promise<ApiError> {
  const requestIdHeader = response.headers.get('X-Request-Id') ?? '';
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const parsed = ErrorEnvelopeSchema.safeParse(payload);
  if (parsed.success) {
    const { code, message, details, request_id } = parsed.data.error;
    return new ApiError({
      code,
      message,
      status: response.status,
      details: details as Record<string, unknown> | undefined,
      requestId: request_id || requestIdHeader,
    });
  }

  return new ApiError({
    code: 'INTERNAL_ERROR',
    message: 'Ocurrió un error inesperado. Inténtalo nuevamente.',
    status: response.status,
    details: undefined,
    requestId: requestIdHeader,
  });
}

/**
 * Reacción a un error de la API: `UNAUTHENTICATED` y `TERMINAL_SESSION_REVOKED` invalidan la
 * credencial (se borra **antes** de emitir el evento, así el gate de `/` ya no la encuentra) y
 * emiten el evento asociado. El detalle nunca lleva tokens ni cabeceras.
 */
export async function notifyApiError(error: ApiError): Promise<void> {
  const eventName = EVENT_BY_ERROR_CODE[error.code];
  if (!eventName) {
    return;
  }
  await clearCredential();
  const detail: FerminEventDetail = {
    code: error.code,
    message: error.message,
    status: error.status,
    requestId: error.requestId,
    ...(error.revokeReason ? { reason: error.revokeReason } : {}),
  };
  emitFerminEvent(eventName, detail);
}

export interface RequestOptions<S extends z.ZodType = z.ZodType> {
  /** Se serializa con `JSON.stringify` una vez. */
  body?: unknown;
  /** Esquema Zod de `@fermin/contracts` para validar la respuesta 2xx. */
  schema?: S;
  signal?: AbortSignal;
  /**
   * `false` para no adjuntar `Authorization` aunque haya credencial (rutas públicas como
   * `POST /activate`, que debe canjear el código sin depender de una sesión anterior). Por
   * defecto `true`.
   */
  auth?: boolean;
}

async function request(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<unknown> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (MUTATING_METHODS.includes(method)) {
    headers[CSRF_HEADER] = TERMINAL_CSRF_HEADER_VALUE;
  }
  const bodyText = options.body === undefined ? undefined : JSON.stringify(options.body);
  if (bodyText !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (options.auth !== false) {
    const token = await getSessionToken();
    if (token !== null) {
      headers[AUTHORIZATION_HEADER] = `Bearer ${token}`;
    }
  }

  const resolvedPath = resolvePath(path);
  const init: RequestInit = {
    method,
    headers,
    ...(bodyText !== undefined ? { body: bodyText } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  let response: Response;
  try {
    response = await fetch(buildUrl(resolvedPath), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause;
    }
    throw new NetworkError(cause);
  }

  if (!response.ok) {
    const error = await toApiError(response);
    await notifyApiError(error);
    throw error;
  }

  if (response.status === 204) {
    return undefined;
  }

  const text = await response.text();
  if (text.length === 0) {
    return undefined;
  }

  const json: unknown = JSON.parse(text);
  return options.schema ? options.schema.parse(json) : undefined;
}

type WithSchema<S extends z.ZodType> = RequestOptions<S> & { schema: S };
type WithoutSchema = Omit<RequestOptions, 'schema'>;

function apiGet<S extends z.ZodType>(path: string, options: WithSchema<S>): Promise<z.output<S>> {
  return request('GET', path, options) as Promise<z.output<S>>;
}

function apiPost<S extends z.ZodType>(path: string, options: WithSchema<S>): Promise<z.output<S>>;
function apiPost(path: string, options?: WithoutSchema): Promise<undefined>;
function apiPost(path: string, options?: RequestOptions): Promise<unknown> {
  return request('POST', path, options);
}

function apiPatch<S extends z.ZodType>(path: string, options: WithSchema<S>): Promise<z.output<S>>;
function apiPatch(path: string, options?: WithoutSchema): Promise<undefined>;
function apiPatch(path: string, options?: RequestOptions): Promise<unknown> {
  return request('PATCH', path, options);
}

function apiDelete<S extends z.ZodType>(path: string, options: WithSchema<S>): Promise<z.output<S>>;
function apiDelete(path: string, options?: WithoutSchema): Promise<undefined>;
function apiDelete(path: string, options?: RequestOptions): Promise<unknown> {
  return request('DELETE', path, options);
}

/** Cliente único de Marker. Toda llamada HTTP a la API pasa por aquí. */
export const api = {
  get: apiGet,
  post: apiPost,
  patch: apiPatch,
  delete: apiDelete,
};
